import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { KeyRound, X } from "lucide-react";
import { api } from "../lib/api";
import safeStorage from "../lib/safeStorage";

const SOFT_REMIND_DAYS = 30;
const DISMISS_KEY = "trel.pw_reminder.dismissed_until";

/**
 * Soft, non-blocking reminder banner.
 * - Shows when the admin's password is ≥ 30 days old (or has never been changed since seed).
 * - Dismissible: dismiss snoozes the reminder for 7 days via localStorage.
 * - Links to /account where the user can rotate their password at their pace.
 */
export default function PasswordAgeReminder() {
  const [info, setInfo] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const changedRaw = data.password_changed_at || data.created_at;
      if (!changedRaw) return;
      const changed = new Date(changedRaw);
      const days = Math.floor((Date.now() - changed.getTime()) / 86400000);
      if (days < SOFT_REMIND_DAYS) return;
      // Honor a 7-day snooze (safeStorage handles SSR + private-mode failures)
      const dismissUntil = Number(safeStorage.get(DISMISS_KEY, "0"));
      if (dismissUntil && Date.now() < dismissUntil) return;
      setInfo({ days });
    } catch {
      /* noop — soft feature */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!info || hidden) return null;

  const snooze = () => {
    safeStorage.set(DISMISS_KEY, String(Date.now() + 7 * 86400000));
    setHidden(true);
  };

  return (
    <div
      data-testid="password-age-reminder"
      className="mt-6 p-4 rounded-2xl border border-[#C9A45C]/40 bg-gradient-to-r from-[#C9A45C]/10 to-transparent flex items-center gap-3 flex-wrap"
    >
      <div className="w-9 h-9 rounded-full bg-[#C9A45C]/20 flex items-center justify-center shrink-0">
        <KeyRound size={16} className="text-[#C9A45C]"/>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
          Security · Recommendation
        </div>
        <div className="text-sm text-[#071A2D] mt-0.5">
          Your password is <strong>{info.days} days old</strong>. We recommend rotating it every 30 days.
        </div>
      </div>
      <Link
        to="/account"
        data-testid="password-age-cta"
        className="text-xs px-4 py-2 rounded-full bg-[#064E3B] text-[#FFF8EE] font-semibold"
      >
        Update password
      </Link>
      <button
        data-testid="password-age-dismiss"
        onClick={snooze}
        className="text-[#071A2D]/55 hover:text-[#071A2D] p-1"
        aria-label="Dismiss for 7 days"
      >
        <X size={16}/>
      </button>
    </div>
  );
}
